/**
 * Interactive Sains content, Bahasa Melayu, aligned to DSKP KSSR Semakan 2017 Sains Tahun 3
 * (db/curriculum/kssr/sains-t1-3.json):
 *  - 3.3.1 Menerangkan proses pencernaan (urutan aliran makanan), 3.3.2 makanan tercerna yang tidak diperlukan badan
 *  - 9.1.1 Menyenaraikan ahli Sistem Suria, 9.1.2 planet beredar mengelilingi Matahari mengikut orbit,
 *    9.1.3 kedudukan planet dari Matahari dengan masa planet beredar (suhu planet mengikut urutan)
 * Pure data and generators; the drawing lives in apps/web.
 */
import { FOTO_EN, FOTO_MS, type FotoText } from "./sainsFoto.js";
import { BAND_EN, CIRI_EN, FOOD_EN, ORBIT_RULE_EN, ORGANS_EN, PLANETS_EN, SUN_EN } from "./sainsEn.js";

/** Content language of the Sains games: Bahasa Melayu (default) or English. */
export type SainsLang = "ms" | "en";
type Rnd = () => number;
function shuffled<T>(a: readonly T[], rnd: Rnd): T[] {
  const b = [...a];
  for (let i = b.length - 1; i > 0; i--) { const j = Math.floor(rnd() * (i + 1)); [b[i], b[j]] = [b[j]!, b[i]!]; }
  return b;
}

/* ── Sistem pencernaan (SP 3.3.1, 3.3.2) ─────────────────────────────────── */
export type OrganId = "mulut" | "esofagus" | "perut" | "usus-kecil" | "usus-besar" | "dubur";
export type Organ = {
  id: OrganId;
  name: string;
  /** One short line shown while the food passes through. */
  tagline: string;
  /** Explanation shown when the organ is tapped and zoomed (2–3 short sentences, Tahun 3 level). */
  detail: string;
  /** The organ's job in one neutral phrase (a quiz answer). */
  fact: string;
  /** A word from `fact` that must appear in `detail`, so the quiz only asks what the explanation says. */
  key: string;
};

/** In the order food travels. */
export const ORGANS: readonly Organ[] = [
  { id: "mulut", name: "Mulut", tagline: "Gigi mengunyah makanan.", detail: "Mulut ialah tempat makanan masuk ke dalam badan. Gigi mengunyah makanan menjadi kecil dan air liur melembutkan makanan supaya mudah ditelan. Seterusnya, makanan masuk ke esofagus.", fact: "Mengunyah dan melembutkan makanan", key: "mengunyah" },
  { id: "esofagus", name: "Esofagus", tagline: "Makanan ditolak ke perut.", detail: "Esofagus ialah tiub panjang yang menyambung mulut dengan perut. Otot esofagus menolak makanan turun. Seterusnya, makanan masuk ke perut.", fact: "Menolak makanan turun", key: "menolak" },
  { id: "perut", name: "Perut", tagline: "Asid dan jus perut menghancurkan makanan.", detail: "Perut mengisar makanan. Asid dan jus perut menghancurkan makanan menjadi cecair pekat. Seterusnya, makanan masuk ke usus kecil.", fact: "Menghancurkan makanan dengan asid", key: "asid" },
  { id: "usus-kecil", name: "Usus kecil", tagline: "Zat makanan diserap ke dalam darah.", detail: "Usus kecil ialah tiub yang panjang dan berlingkar. Zat makanan diserap ke dalam darah. Darah membawa zat makanan ke seluruh badan. Seterusnya, baki makanan masuk ke usus besar.", fact: "Menyerap zat makanan ke dalam darah", key: "diserap ke dalam darah" },
  { id: "usus-besar", name: "Usus besar", tagline: "Air diserap, sisa menjadi najis.", detail: "Usus besar menyerap air daripada sisa makanan. Makanan yang tidak dicerna dan tidak diperlukan oleh badan menjadi najis. Seterusnya, najis menuju ke dubur.", fact: "Menyerap air daripada sisa makanan", key: "menyerap air" },
  { id: "dubur", name: "Dubur", tagline: "Najis dikeluarkan daripada badan.", detail: "Najis dikeluarkan daripada badan melalui dubur. Inilah penghujung perjalanan makanan. Buang air besar setiap hari menjaga badan sihat.", fact: "Mengeluarkan najis daripada badan", key: "dikeluarkan" },
];

export const ORGAN_IDS: readonly OrganId[] = ORGANS.map((o) => o.id);
/** The organs with their text in the chosen language (order, ids and drawing stay the same). */
export const organsOf = (lang: SainsLang = "ms"): readonly Organ[] => (lang === "en" ? ORGANS.map((o) => ({ ...o, ...ORGANS_EN[o.id] })) : ORGANS);
export const foodName = (id: string, ms: string, lang: SainsLang = "ms") => (lang === "en" ? FOOD_EN[id] ?? ms : ms);

export type Makanan = { id: string; name: string; colour: string };
export const MAKANAN: readonly Makanan[] = [
  { id: "roti", name: "Roti", colour: "#e8b25c" },
  { id: "epal", name: "Epal", colour: "#e5484d" },
  { id: "nasi", name: "Nasi", colour: "#f4f1e8" },
  { id: "pisang", name: "Pisang", colour: "#f6d83b" },
];

/** One quiz question. Every question is generated from the same text the pupil has just read (see tests: each answer is traced back to the explanation). */
export type QuizQ = { id: string; cat: string; prompt: string; options: string[]; answer: string };

/** Pick `n` questions: all different, different answers where possible, spread across categories. */
export function pickQuiz(pool: readonly QuizQ[], n: number, rnd: Rnd = Math.random): QuizQ[] {
  const cats = shuffled([...new Set(pool.map((q) => q.cat))], rnd);
  const by = new Map<string, QuizQ[]>(cats.map((c) => [c, shuffled(pool.filter((q) => q.cat === c), rnd)]));
  const out: QuizQ[] = [];
  const answers = new Set<string>();
  const prompts = new Set<string>();
  for (const strict of [true, false]) {
    let progressed = true;
    while (out.length < n && progressed) {
      progressed = false;
      for (const c of cats) {
        if (out.length >= n) break;
        const list = by.get(c)!;
        const i = list.findIndex((q) => !prompts.has(q.prompt) && (!strict || !answers.has(q.answer)));
        if (i < 0) continue;
        const [q] = list.splice(i, 1);
        out.push(q!); answers.add(q!.answer); prompts.add(q!.prompt);
        progressed = true;
      }
    }
  }
  return shuffled(out, rnd);
}

const choose = (answer: string, others: readonly string[], rnd: Rnd, count = 3) =>
  shuffled([answer, ...shuffled([...new Set(others)].filter((x) => x !== answer), rnd).slice(0, count)], rnd);

const lower = (name: string) => name.toLowerCase();

const PQ = {
  ms: {
    urutan: (n: string) => `Selepas ${n}, ke manakah perjalanan makanan seterusnya?`, tugas: (n: string) => `Apakah tugas ${n}?`,
    mulut: "Makanan masuk ke dalam badan melalui…", perut: "Di manakah asid dan jus perut menghancurkan makanan?", kecil: "Di manakah zat makanan diserap ke dalam darah?",
    besar: "Di manakah air diserap daripada sisa makanan?", dubur: "Najis dikeluarkan daripada badan melalui…",
    sisa: "Apakah yang berlaku kepada makanan yang tidak diperlukan oleh badan?", sisaAns: ["Keluar sebagai najis", "Menjadi darah", "Disimpan dalam otak", "Menjadi tulang"],
  },
  en: {
    urutan: (n: string) => `After the ${n}, where does the food go next?`, tugas: (n: string) => `What does the ${n} do?`,
    mulut: "Food enters the body through the…", perut: "Where do acid and stomach juice break the food down?", kecil: "Where are nutrients absorbed into the blood?",
    besar: "Where is water absorbed from the leftover food?", dubur: "Faeces leave the body through the…",
    sisa: "What happens to food that the body does not need?", sisaAns: ["It leaves as faeces", "It becomes blood", "It is stored in the brain", "It becomes bones"],
  },
} as const;

/** Pool for Perjalanan Makanan, built from the organs (SP 3.3.1 urutan, 3.3.2 makanan tidak diperlukan menjadi najis). */
export function pencernaanPool(rnd: Rnd = Math.random, lang: SainsLang = "ms"): QuizQ[] {
  const organs = organsOf(lang);
  const T = PQ[lang];
  const names = organs.map((o) => o.name);
  const qs: QuizQ[] = [];
  organs.forEach((o, i) => {
    const next = organs[i + 1];
    if (next) qs.push({ id: `urutan-${o.id}`, cat: "urutan", prompt: T.urutan(lower(o.name)), answer: next.name, options: choose(next.name, names.filter((n) => n !== o.name), rnd) });
    qs.push({ id: `tugas-${o.id}`, cat: "tugas", prompt: T.tugas(lower(o.name)), answer: o.fact, options: choose(o.fact, organs.filter((x) => x.id !== o.id).map((x) => x.fact), rnd) });
  });
  const place = (id: OrganId, prompt: string) => {
    const o = organs.find((x) => x.id === id)!;
    qs.push({ id: `tempat-${id}`, cat: "tempat", prompt, answer: o.name, options: choose(o.name, names, rnd) });
  };
  place("mulut", T.mulut);
  place("perut", T.perut);
  place("usus-kecil", T.kecil);
  place("usus-besar", T.besar);
  place("dubur", T.dubur);
  qs.push({ id: "sisa", cat: "sisa", prompt: T.sisa, answer: T.sisaAns[0], options: shuffled(T.sisaAns, rnd) });
  return qs;
}
export const pencernaanQuiz = (n = 6, rnd: Rnd = Math.random, lang: SainsLang = "ms") => pickQuiz(pencernaanPool(rnd, lang), n, rnd);

/* ── Sistem Suria (SP 9.1.1–9.1.3) ───────────────────────────────────────── */
export type PlanetId = "utarid" | "zuhrah" | "bumi" | "marikh" | "musytari" | "zuhal" | "uranus" | "neptun";
export type Planet = {
  id: PlanetId;
  name: string;
  /** Orbital period around the Sun, in Earth days (rounded). */
  orbitDays: number;
  /** Typical surface / cloud-top temperature in °C (rounded, one representative figure). */
  tempC: number;
  /** One-word temperature band for the Tahun 3 "suhu planet mengikut urutan" activity. */
  tempBand: string;
  colour: string;
  colour2: string;
  /** Relative drawn size for the orbit view (not to scale) and the zoom view. */
  size: number;
  rings?: boolean;
  /** One line that identifies the planet (also a quiz clue). */
  tagline: string;
  /** Two sentences: where it is, what it is, how long one orbit takes. */
  detail: string;
  /** Short facts (size, surface or atmosphere, moons, temperature). */
  facts: readonly string[];
};

/** In order from the Sun. Figures are NASA rounded values. */
export const PLANETS: readonly Planet[] = [
  { id: "utarid", name: "Utarid", orbitDays: 88, tempC: 167, tempBand: "Sangat panas", colour: "#b8a999", colour2: "#6f6459", size: 5, tagline: "Planet paling dekat dengan Matahari", detail: "Utarid ialah planet pertama dari Matahari dan planet paling kecil. Kerana paling dekat, Utarid beredar paling laju, hanya 88 hari untuk satu edaran.", facts: ["Siang di Utarid sangat panas, tetapi malamnya sangat sejuk kerana atmosferanya terlalu nipis untuk menyimpan haba.", "Permukaan Utarid berbatu dan penuh kawah.", "Utarid tidak mempunyai bulan."] },
  { id: "zuhrah", name: "Zuhrah", orbitDays: 225, tempC: 464, tempBand: "Sangat panas", colour: "#f0cf86", colour2: "#b97f35", size: 8, tagline: "Planet paling panas", detail: "Zuhrah ialah planet kedua dari Matahari dan saiznya hampir sama dengan Bumi. Walaupun bukan yang paling dekat, Zuhrah ialah planet paling panas dan beredar dalam 225 hari.", facts: ["Awan tebal memerangkap haba Matahari, jadi suhu Zuhrah kira-kira 464°C.", "Zuhrah kelihatan sangat terang di langit, dikenali sebagai bintang pagi atau bintang petang.", "Zuhrah tidak mempunyai bulan."] },
  { id: "bumi", name: "Bumi", orbitDays: 365, tempC: 15, tempBand: "Selesa", colour: "#4f9de8", colour2: "#2a6b3d", size: 8.5, tagline: "Planet tempat kita tinggal", detail: "Bumi ialah planet ketiga dari Matahari. Bumi mempunyai air, udara dan suhu yang sesuai untuk hidupan, dan beredar mengelilingi Matahari dalam 365 hari, iaitu satu tahun.", facts: ["Lebih kurang 71% permukaan Bumi diliputi air.", "Udara Bumi mengandungi oksigen yang kita sedut untuk bernafas.", "Bumi mempunyai satu bulan."] },
  { id: "marikh", name: "Marikh", orbitDays: 687, tempC: -65, tempBand: "Sejuk", colour: "#e07b4f", colour2: "#8f3d22", size: 6.5, tagline: "Planet merah", detail: "Marikh ialah planet keempat dari Matahari. Permukaannya berdebu dan berwarna merah kerana mengandungi besi berkarat, dan satu edarannya mengambil kira-kira 687 hari.", facts: ["Marikh sejuk, suhu puratanya kira-kira -65°C.", "Atmosfera Marikh nipis dan kebanyakannya karbon dioksida.", "Marikh mempunyai dua bulan kecil.", "Gunung berapi tertinggi dalam Sistem Suria, Olympus Mons, berada di Marikh."] },
  { id: "musytari", name: "Musytari", orbitDays: 4333, tempC: -110, tempBand: "Sangat sejuk", colour: "#e8c9a0", colour2: "#b5764a", size: 17, tagline: "Planet paling besar", detail: "Musytari ialah planet kelima dari Matahari dan planet paling besar dalam Sistem Suria. Kerana jauh dari Matahari, satu edarannya mengambil kira-kira 12 tahun Bumi.", facts: ["Musytari diperbuat daripada gas, terutamanya hidrogen dan helium, dan tidak mempunyai permukaan pepejal.", "Bintik Merah Besar di Musytari ialah ribut yang sangat besar.", "Suhu awan Musytari kira-kira -110°C.", "Musytari mempunyai banyak bulan."] },
  { id: "zuhal", name: "Zuhal", orbitDays: 10759, tempC: -140, tempBand: "Sangat sejuk", colour: "#ecd9a6", colour2: "#b39a5a", size: 14.5, rings: true, tagline: "Planet yang mempunyai cincin", detail: "Zuhal ialah planet keenam dari Matahari dan terkenal dengan cincinnya yang indah. Zuhal mengambil kira-kira 29 tahun Bumi untuk satu edaran.", facts: ["Cincin Zuhal terdiri daripada kepingan ais dan batu.", "Zuhal diperbuat daripada gas, terutamanya hidrogen dan helium, dan ketumpatannya kurang daripada air.", "Suhu Zuhal kira-kira -140°C.", "Zuhal mempunyai banyak bulan."] },
  { id: "uranus", name: "Uranus", orbitDays: 30687, tempC: -195, tempBand: "Sangat sejuk", colour: "#a6e3e9", colour2: "#5fb4c0", size: 11, tagline: "Planet biru kehijauan yang sangat sejuk", detail: "Uranus ialah planet ketujuh dari Matahari. Ia sangat jauh dan sangat sejuk, dan satu edarannya mengambil kira-kira 84 tahun Bumi.", facts: ["Warna biru kehijauan Uranus datang daripada gas metana dalam atmosferanya.", "Uranus berputar secara mengiring, seperti bola yang bergolek.", "Suhu Uranus kira-kira -195°C.", "Uranus mempunyai cincin yang nipis dan banyak bulan."] },
  { id: "neptun", name: "Neptun", orbitDays: 60190, tempC: -200, tempBand: "Sangat sejuk", colour: "#5b7ff0", colour2: "#2a3fa3", size: 10.5, tagline: "Planet paling jauh dari Matahari", detail: "Neptun ialah planet kelapan dan paling jauh dari Matahari. Kerana paling jauh, Neptun mengambil masa paling lama untuk beredar, kira-kira 165 tahun Bumi.", facts: ["Neptun berwarna biru dan sangat sejuk, kira-kira -200°C.", "Angin di Neptun sangat kencang, antara yang paling laju dalam Sistem Suria.", "Neptun mempunyai beberapa bulan."] },
];

/** The planets with their text in the chosen language (figures, colours and order stay the same). */
export const planetsOf = (lang: SainsLang = "ms"): readonly Planet[] =>
  lang === "en" ? PLANETS.map((p) => ({ ...p, ...PLANETS_EN[p.id], tempBand: BAND_EN[p.tempBand as keyof typeof BAND_EN] ?? p.tempBand })) : PLANETS;

export const SUN_DETAIL = "Matahari ialah bintang di tengah Sistem Suria. Semua planet beredar mengelilingi Matahari mengikut orbit masing-masing. Matahari memberi cahaya dan haba.";
export const SUN_FACTS: readonly string[] = [
  "Sistem Suria mempunyai 8 planet: Utarid, Zuhrah, Bumi, Marikh, Musytari, Zuhal, Uranus dan Neptun.",
  "Matahari ialah bintang, bukan planet.",
  "Matahari jauh lebih besar daripada semua planet.",
  "Permukaan Matahari sangat panas, kira-kira 5,500°C.",
];
/** Printed under every planet: the rule from SP 9.1.3. */
export const ORBIT_RULE = "Semakin jauh planet dari Matahari, semakin lama masa untuk beredar satu kali.";
export const SUN_TAGLINE = "Bintang di tengah Sistem Suria";
/** Name, one-line tagline, explanation and fact list of the Sun. */
export const sunOf = (lang: SainsLang = "ms") => (lang === "en" ? SUN_EN : { name: "Matahari", tagline: SUN_TAGLINE, detail: SUN_DETAIL, facts: SUN_FACTS });
export const orbitRule = (lang: SainsLang = "ms") => (lang === "en" ? ORBIT_RULE_EN : ORBIT_RULE);

/** Human text for an orbit length, e.g. "88 hari" or "kira-kira 12 tahun". */
export function orbitText(days: number, lang: SainsLang = "ms"): string {
  if (lang === "en") return days < 700 ? `${days} days` : `about ${Math.round(days / 365)} years`;
  if (days < 700) return `${days} hari`;
  return `kira-kira ${Math.round(days / 365)} tahun`;
}

/**
 * Angular speed (radians per second) for the animation. Real periods differ by ×700, so the animation compresses them
 * with period^0.6 — the order is preserved (nearer = faster) which is what SP 9.1.3 asks pupils to notice.
 */
export function orbitSpeed(days: number, secondsForEarth = 14): number {
  const earth = 365 ** 0.6;
  return (2 * Math.PI / secondsForEarth) * (earth / days ** 0.6);
}

/** Facts a planet is asked about by name, with a phrase that must appear in its explanation (checked by tests). */
export const CIRI: readonly { id: PlanetId; prompt: string; needle: string }[] = [
  { id: "zuhal", prompt: "Planet manakah mempunyai cincin yang terdiri daripada ais dan batu?", needle: "ais dan batu" },
  { id: "marikh", prompt: "Planet manakah berwarna merah kerana besi berkarat?", needle: "besi berkarat" },
  { id: "musytari", prompt: "Planet manakah mempunyai Bintik Merah Besar?", needle: "Bintik Merah Besar" },
  { id: "uranus", prompt: "Planet manakah berputar secara mengiring?", needle: "mengiring" },
  { id: "bumi", prompt: "Planet manakah mempunyai udara dengan oksigen untuk kita bernafas?", needle: "oksigen" },
  { id: "utarid", prompt: "Planet manakah permukaannya berbatu dan penuh kawah?", needle: "kawah" },
  { id: "zuhrah", prompt: "Planet manakah dikenali sebagai bintang pagi atau bintang petang?", needle: "bintang pagi" },
  { id: "neptun", prompt: "Planet manakah anginnya sangat kencang dan berwarna biru?", needle: "Angin di Neptun" },
];

/** CIRI in the chosen language. */
export const ciriOf = (lang: SainsLang = "ms") => (lang === "en" ? CIRI.map((c) => ({ ...c, ...CIRI_EN[c.id] })) : CIRI);

const SQ = {
  ms: {
    klu: (tag: string) => `${tag}. Apakah nama planet ini?`, nombor: (n: number) => `Planet nombor ${n} dari Matahari ialah…`,
    edar: (name: string) => `Berapa lama ${name} beredar mengelilingi Matahari satu kali?`, suhu: (name: string) => `Bagaimanakah suhu ${name}?`,
    laju: "Planet manakah beredar paling laju mengelilingi Matahari?", lama: "Planet manakah mengambil masa paling lama untuk mengelilingi Matahari?",
    besar: "Planet manakah paling besar dalam Sistem Suria?", panas: "Planet manakah paling panas?",
    jauh: "Semakin jauh planet dari Matahari, masa untuk beredar satu kali menjadi…", jauhAns: ["Semakin lama", "Semakin singkat", "Sama sahaja", "Berubah-ubah"],
    ahli8: "Berapakah bilangan planet dalam Sistem Suria?", bintang: "Apakah Matahari?", bintangAns: ["Bintang", "Planet", "Bulan", "Awan"],
    orbit: "Planet beredar mengelilingi Matahari mengikut…", orbitAns: ["Orbit masing-masing", "Garisan lurus", "Bulan", "Angin"],
    bands: ["Sangat panas", "Panas", "Selesa", "Sejuk", "Sangat sejuk"],
  },
  en: {
    klu: (tag: string) => `${tag}. What is the name of this planet?`, nombor: (n: number) => `Planet number ${n} from the Sun is…`,
    edar: (name: string) => `How long does ${name} take to orbit the Sun once?`, suhu: (name: string) => `How is the temperature on ${name}?`,
    laju: "Which planet orbits the Sun the fastest?", lama: "Which planet takes the longest to orbit the Sun?",
    besar: "Which planet is the biggest in the Solar System?", panas: "Which planet is the hottest?",
    jauh: "The farther a planet is from the Sun, the time it takes to orbit once becomes…", jauhAns: ["Longer", "Shorter", "The same", "Always changing"],
    ahli8: "How many planets are there in the Solar System?", bintang: "What is the Sun?", bintangAns: ["A star", "A planet", "A moon", "A cloud"],
    orbit: "Planets move around the Sun along…", orbitAns: ["Their own orbits", "Straight lines", "The Moon", "The wind"],
    bands: ["Very hot", "Hot", "Pleasant", "Cold", "Very cold"],
  },
} as const;

/** Pool for Sistem Suria, built from the planets (SP 9.1.1 ahli, 9.1.2 orbit, 9.1.3 kedudukan dan masa edaran, suhu). */
export function suriaPool(rnd: Rnd = Math.random, lang: SainsLang = "ms"): QuizQ[] {
  const planets = planetsOf(lang);
  const T = SQ[lang];
  const names = planets.map((p) => p.name);
  const nameOf = (id: PlanetId) => planets.find((p) => p.id === id)!.name;
  const qs: QuizQ[] = [];
  planets.forEach((p, i) => {
    qs.push({ id: `klu-${p.id}`, cat: "klu", prompt: T.klu(p.tagline), answer: p.name, options: choose(p.name, names, rnd) });
    qs.push({ id: `nombor-${p.id}`, cat: "kedudukan", prompt: T.nombor(i + 1), answer: p.name, options: choose(p.name, names, rnd) });
    qs.push({ id: `edar-${p.id}`, cat: "edaran", prompt: T.edar(p.name), answer: orbitText(p.orbitDays, lang), options: choose(orbitText(p.orbitDays, lang), planets.map((x) => orbitText(x.orbitDays, lang)), rnd) });
    qs.push({ id: `suhu-${p.id}`, cat: "suhu", prompt: T.suhu(p.name), answer: p.tempBand, options: choose(p.tempBand, T.bands, rnd) });
  });
  for (const c of ciriOf(lang)) qs.push({ id: `ciri-${c.id}`, cat: "ciri", prompt: c.prompt, answer: nameOf(c.id), options: choose(nameOf(c.id), names, rnd) });
  const rel = (id: string, prompt: string, answer: PlanetId) => qs.push({ id, cat: "hubungan", prompt, answer: nameOf(answer), options: choose(nameOf(answer), names, rnd) });
  rel("rel-laju", T.laju, "utarid");
  rel("rel-lama", T.lama, "neptun");
  rel("rel-besar", T.besar, "musytari");
  rel("rel-panas", T.panas, "zuhrah");
  qs.push({ id: "rel-jauh", cat: "hubungan", prompt: T.jauh, answer: T.jauhAns[0], options: shuffled(T.jauhAns, rnd) });
  qs.push({ id: "ahli-8", cat: "ahli", prompt: T.ahli8, answer: "8", options: shuffled(["6", "7", "8", "9"], rnd) });
  qs.push({ id: "ahli-bintang", cat: "ahli", prompt: T.bintang, answer: T.bintangAns[0], options: shuffled(T.bintangAns, rnd) });
  qs.push({ id: "ahli-orbit", cat: "ahli", prompt: T.orbit, answer: T.orbitAns[0], options: shuffled(T.orbitAns, rnd) });
  return qs;
}
export const suriaQuiz = (n = 6, rnd: Rnd = Math.random, lang: SainsLang = "ms") => pickQuiz(suriaPool(rnd, lang), n, rnd);

/* ── Fotosintesis (3D game: pokok membuat makanan sendiri) ─────────────────── */

export type { FotoText } from "./sainsFoto.js";
export const fotoOf = (lang: SainsLang = "ms"): FotoText => (lang === "en" ? FOTO_EN : FOTO_MS);
export function fotoPool(rnd: Rnd = Math.random, lang: SainsLang = "ms"): QuizQ[] {
  return fotoOf(lang).quiz.map((q) => ({ id: `foto-${q.id}`, cat: q.cat, prompt: q.prompt, answer: q.answer, options: shuffled([q.answer, ...q.wrong], rnd) }));
}
export const fotoQuiz = (n = 6, rnd: Rnd = Math.random, lang: SainsLang = "ms") => pickQuiz(fotoPool(rnd, lang), n, rnd);


/* ── Spoken form (for the pre-generated Gemini audio) ─────────────────────── */

const ONES = ["kosong", "satu", "dua", "tiga", "empat", "lima", "enam", "tujuh", "lapan", "sembilan"];
/** Malay words for 0–9999: 12 → "dua belas", 365 → "tiga ratus enam puluh lima". */
export function malayNumber(n: number): string {
  if (!Number.isInteger(n) || n < 0 || n > 9999) throw new Error(`malayNumber: ${n} out of range`);
  if (n < 10) return ONES[n]!;
  if (n === 10) return "sepuluh";
  if (n === 11) return "sebelas";
  if (n < 20) return `${ONES[n - 10]} belas`;
  if (n < 100) return `${ONES[Math.floor(n / 10)]} puluh${n % 10 ? " " + ONES[n % 10] : ""}`;
  if (n < 1000) return `${n < 200 ? "seratus" : ONES[Math.floor(n / 100)] + " ratus"}${n % 100 ? " " + malayNumber(n % 100) : ""}`;
  return `${n < 2000 ? "seribu" : ONES[Math.floor(n / 1000)] + " ribu"}${n % 1000 ? " " + malayNumber(n % 1000) : ""}`;
}

/** Text as it should be READ: digits, °C and % written out, "…" dropped. The screen keeps the original. */
export function spoken(text: string, lang: SainsLang = "ms"): string {
  const ms = lang === "ms";
  return text
    .replace(/(^|[\s(])-(\d)/g, ms ? "$1negatif $2" : "$1minus $2")
    .replace(/(\d[\d,]*)\s*°C/g, (_, n: string) => (ms ? `${malayNumber(Number(n.replace(/,/g, "")))} darjah Celcius` : `${n} degrees Celsius`))
    .replace(/(\d[\d,]*)\s*%/g, (_, n: string) => (ms ? `${malayNumber(Number(n.replace(/,/g, "")))} peratus` : `${n} percent`))
    .replace(/\d[\d,]*/g, (n) => (ms ? malayNumber(Number(n.replace(/,/g, ""))) : n))
    .replace(/…/g, "")
    .replace(/\s*[:;]\s*/g, ", ")
    .replace(/[“”"()]/g, "")
    .replace(/\s+([.,!?])/g, "$1")
    .replace(/,\s*([.,!?])/g, "$1")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/** Sentences of a paragraph, each read as its own clip. */
export const sentencesOf = (text: string): string[] => text.split(/(?<=[.!?])\s+/).map((x) => x.trim()).filter(Boolean);

/** What the page reads aloud when its speaker button is tapped: the heading, then each sentence. */
export const organScript = (o: Organ): string[] => [o.name, ...sentencesOf(o.detail)];
export const planetScript = (p: Planet): string[] => [p.name, `${p.tagline}.`, ...sentencesOf(p.detail), ...p.facts];
export const sunScript = (lang: SainsLang = "ms"): string[] => { const u = sunOf(lang); return [u.name, ...sentencesOf(u.detail), ...u.facts]; };
export const SUN_SCRIPT: readonly string[] = sunScript("ms");
export const fotoScript = (lang: SainsLang = "ms"): string[] => { const f = fotoOf(lang); return [f.name, `${f.tagline}.`, ...sentencesOf(f.detail), ...f.facts]; };

/** Every text the Sains games read aloud, as spoken form. `word` = a name; `line` = a sentence or question. */
export function sainsTexts(lang: SainsLang = "ms"): { kind: "word" | "line"; text: string }[] {
  const out: { kind: "word" | "line"; text: string }[] = [];
  const add = (kind: "word" | "line", t: string) => out.push({ kind, text: spoken(t, lang) });
  const script = (lines: readonly string[]) => lines.forEach((l, i) => add(i === 0 ? "word" : "line", l));
  organsOf(lang).forEach((o) => script(organScript(o)));
  planetsOf(lang).forEach((p) => script(planetScript(p)));
  script(sunScript(lang));
  script(fotoScript(lang));
  add("line", orbitRule(lang));
  for (const q of [...pencernaanPool(Math.random, lang), ...suriaPool(Math.random, lang), ...fotoPool(Math.random, lang)]) add("line", q.prompt);
  const seen = new Set<string>();
  return out.filter((x) => (seen.has(x.kind + x.text) ? false : (seen.add(x.kind + x.text), true)));
}
